import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { GlobalThemeColors } from '~/@types/colors'
import { serializeAlgoliaResults } from '~/lib/algolia/serialize-algolia-results'

import { Spinner } from '@unlikelystudio/react-abstract-components'

import { ProductCardProps } from '~/components/UI/ProductCard'
import SearchInput from '~/components/UI/SearchContent/SearchInput'
import SearchRecommendedProducts from '~/components/UI/SearchContent/SearchRecommendedProducts'
import SearchResultsGrid from '~/components/UI/SearchContent/SearchResultsGrid'

import useAlgoliaSearchQuery from '~/hooks/useAlgoliaSearchQuery'
import useDebounce from '~/hooks/useDebounce'
import useLocale from '~/hooks/useLocale'

import getAlgoliaAutocompleteResult from '~/utils/get-algolia-autocomplete-result'
import { getQueryParams } from '~/utils/get-query-params'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type UrlParamsProps = {
  q?: string
}
export type SearchContentProps = {
  className?: string
  upsales?: ProductCardProps[]
}

const HITS_PER_PAGE = 8

function SearchContent({ className, upsales }: SearchContentProps) {
  const [isSearching, setIsSearching] = useState(false)
  const router = useRouter()
  const locale = useLocale()

  const params = useMemo<UrlParamsProps>(() => getQueryParams(), [router])
  const { q } = params
  const query = q?.toString() ?? ''

  const debouncedValue = useDebounce(query, 150) ?? null
  const sanitizedDebouncedValue =
    debouncedValue && debouncedValue?.length > 0
      ? debouncedValue?.replaceAll('+', ' ')
      : ''

  useEffect(() => {
    setIsSearching(debouncedValue?.length >= 3)
  }, [debouncedValue])

  const {
    data,
    isFetching,
    isSuccess,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useAlgoliaSearchQuery({
    q: debouncedValue,
    locale,
    key: 'search_products',
    hitsPerPage: HITS_PER_PAGE,
    enabled: isSearching,
  })

  const debouncedNbPages = data?.pages?.length ?? 0
  const debouncedNbHits = data?.pages?.[0]?.nbHits ?? 0

  const memoizedDebouncedAlgoliaData = useMemo(
    () => ({
      debouncedNbHits: debouncedNbHits?.toString(),
      debouncedNbPages: debouncedNbPages?.toString(),
      debouncedValue,
    }),
    [debouncedNbHits, debouncedNbPages, debouncedValue],
  )

  const results = isSearching
    ? data?.pages?.reduce((acc, page) => {
        return [
          ...serializeAlgoliaResults(acc),
          ...serializeAlgoliaResults(page.data),
        ]
      }, []) ?? []
    : []

  const autocompleteResult =
    results && sanitizedDebouncedValue?.length
      ? getAlgoliaAutocompleteResult(
          results?.[0],
          sanitizedDebouncedValue,
          'em',
        )
      : null

  const suggestedValue = autocompleteResult
    ? `${sanitizedDebouncedValue}${autocompleteResult}`
    : null

  const hasResults = isSuccess && !isFetching && results?.length > 0

  return (
    <div className={cx(css.SearchContent, className)}>
      <div className={cx(css.top, { isSearching })}>
        <SearchInput
          hasAutocomplete
          isSearching={isSearching}
          isFetching={isFetching}
          autocompleteValue={autocompleteResult}
          suggestedValue={suggestedValue}
          inputClassName={css.mainInput}
          className={css.input}
        />
      </div>
      {(hasResults || isFetching) && (
        <div className={cx(css.body)}>
          {hasResults && (
            <SearchResultsGrid
              results={results}
              nbHits={data?.pages?.[0]?.nbHits}
              searchQuery={sanitizedDebouncedValue}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          )}
          {isFetching && (
            <div className={css.spinnerWrapper}>
              <Spinner
                className={css.spinner}
                color={GlobalThemeColors.Black}
              />
            </div>
          )}
        </div>
      )}
      {!hasResults && (
        <div className={css.bottom}>
          <SearchRecommendedProducts
            upsales={upsales}
            hasNoResults={isSearching}
          />
        </div>
      )}
    </div>
  )
}

SearchContent.defaultProps = {}

export default SearchContent
